import React, { useEffect, useMemo, useState } from 'react';
import Swal from 'sweetalert2';
import { Row, Col, Button } from 'reactstrap';
import Select from 'react-select';
import moment from 'moment';
import Sidebar from '../../Sidebar';
import { useNavigate } from 'react-router';
import { useCookies } from 'react-cookie';
import FloatingControls from '../../components/FloatingControls';
import FormPageLayout from '../../components/FormPageLayout';
import LoadingOverlay from '../../components/LoadingOverlay';
import ErrorToast from '../../ErrorToast';
import { displayError } from '../../helpers';
import '../../button.css';
import { getCurrentDate, getCurrentTime } from '../../utils/DateUtils';
import { getUserAllowedPlants } from '../../services/PlantServices';
import { getAllProducts } from '../../services/ProductServices';
import { getAllCustomerMain } from '../../services/CustomerMainServices';
import { getAllCustomerSites } from '../../services/CustomerSiteServices';
import TableRowsConsumption from './TableRowsConsumption';
import {
    createConsumption,
    getConsumptionNumber,
    getConsumptionPreview,
    getProductionOptions,
} from '../../services/ConsumptionNoteServices';

const MODE_OPTIONS = [
    { value: 'datewise', label: 'Datewise' },
    { value: 'batchwise', label: 'Batchwise' },
    { value: 'customerwise', label: 'Customerwise' },
    { value: 'sitewise', label: 'Sitewise' },
];

const PRODUCTION_OPTIONS = [
    { value: 'auto', label: 'Auto (from batch report)' },
    { value: 'manual', label: 'Manual (from design mix)' },
];

function emptyLine(lineNo = 1) {
    return {
        line_no: lineNo,
        product_id: '',
        product_name: '',
        material_label: '',
        design_mix_qty: 0,
        consumed_qty: 0,
        unit: 'Kg',
    };
}

function ConsumptionNote() {
    const [cookies] = useCookies(['myToken']);
    const navigate = useNavigate();
    const [plants, setPlants] = useState([]);
    const [products, setProducts] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [sites, setSites] = useState([]);
    const [dcOptions, setDcOptions] = useState([]);
    const [rowsData, setRowsData] = useState([]);
    const [inProgress, setInProgress] = useState(false);
    const [inputs, setInputs] = useState({
        plant_id: '',
        consumption_date: getCurrentDate(),
        consumption_time: getCurrentTime(),
        consumption_no: '',
        prefix: '',
        consumption_mode: 'datewise',
        production_mode: 'auto',
        from_date: getCurrentDate(),
        upto_date: getCurrentDate(),
        customer_id: '',
        consignee_id: '',
        del_challan_id: '',
        total_concrete_qty: 0,
        remarks: '',
    });

    useEffect(() => {
        getUserAllowedPlants(cookies).then((response) => {
            setPlants((response.plant_list || []).filter((p) => p.status).map((p) => ({
                value: p.id,
                label: p.plant_alias,
            })));
        });
        getAllProducts(cookies).then((response) => {
            setProducts(response.product_list || response.product_data || response.products || []);
        });
        getAllCustomerMain(cookies).then((response) => {
            const list = response.customer_list || response.customer_data || response.customers || [];
            setCustomers(list.filter((c) => c.status).map((c) => ({
                value: c.id,
                label: c.entity_name || c.name,
            })));
        });
        getAllCustomerSites(cookies).then((response) => {
            const list = response.consignee_list || response.consignee_data || response.consignees || [];
            setSites(list.filter((s) => s.status).map((s) => ({
                value: s.id,
                label: s.project_name || s.name || s.entity_name,
            })));
        });
    }, [cookies]);

    const loadNumber = (plantId, date) => {
        if (!plantId || !date) return;
        const displayDate = moment(date, 'YYYY-MM-DD').format('DD-MM-YYYY');
        getConsumptionNumber(cookies, plantId, displayDate)
            .then((res) => {
                setInputs((prev) => ({
                    ...prev,
                    consumption_no: res.consumption_no,
                    prefix: res.prefix,
                }));
            })
            .catch((error) => displayError(error?.response?.data || error, 'Number'));
    };

    useEffect(() => {
        if (inputs.plant_id && inputs.consumption_date) {
            loadNumber(inputs.plant_id, inputs.consumption_date);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [inputs.plant_id, inputs.consumption_date]);

    const loadDcOptions = () => {
        if (!inputs.plant_id) return;
        getProductionOptions(cookies, {
            plant_id: inputs.plant_id,
            from_date: moment(inputs.from_date, 'YYYY-MM-DD').format('DD-MM-YYYY'),
            upto_date: moment(inputs.upto_date, 'YYYY-MM-DD').format('DD-MM-YYYY'),
        }).then((res) => {
            setDcOptions((res.delivery_challans || []).map((dc) => ({
                value: dc.id,
                label: `${dc.dc_no} | ${dc.dc_date} | Batch ${dc.batch_no} | ${dc.production_qty} cum`,
            })));
        });
    };

    useEffect(() => {
        if (inputs.consumption_mode === 'batchwise' && inputs.plant_id) {
            loadDcOptions();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [inputs.consumption_mode, inputs.plant_id, inputs.from_date, inputs.upto_date]);

    const handleChange = (event) => {
        const { name, value } = event.target;
        setInputs((prev) => ({ ...prev, [name]: value }));
    };

    const handleSelectChange = (name, option) => {
        setInputs((prev) => ({ ...prev, [name]: option?.value || '' }));
    };

    const previewParams = useMemo(() => ({
        plant_id: inputs.plant_id,
        consumption_mode: inputs.consumption_mode,
        production_mode: inputs.production_mode,
        from_date: moment(inputs.from_date, 'YYYY-MM-DD').format('DD-MM-YYYY'),
        upto_date: moment(inputs.upto_date, 'YYYY-MM-DD').format('DD-MM-YYYY'),
        del_challan_id: inputs.del_challan_id || '',
        customer_id: inputs.customer_id || '',
        consignee_id: inputs.consignee_id || '',
    }), [inputs]);

    const handleLoadFromProduction = () => {
        if (!inputs.plant_id) {
            Swal.fire('Plant required', 'Please select plant first.', 'warning');
            return;
        }
        setInProgress(true);
        getConsumptionPreview(cookies, previewParams)
            .then((res) => {
                if (!res.detail_lines?.length) {
                    Swal.fire('No data', res.message || 'No production records found.', 'info');
                    return;
                }
                setRowsData(res.detail_lines.map((line, idx) => ({ ...line, line_no: idx + 1 })));
                setInputs((prev) => ({ ...prev, total_concrete_qty: res.total_concrete_qty || 0 }));
                Swal.fire('Loaded', `${res.dc_count} delivery challan(s) processed.`, 'success');
            })
            .catch((error) => displayError(error?.response?.data || error, 'Load Failed'))
            .finally(() => setInProgress(false));
    };

    const handleAddLine = () => {
        setRowsData((rows) => [...rows, emptyLine(rows.length + 1)]);
    };

    const handleRemoveLine = (index) => {
        setRowsData((rows) => rows.filter((_, i) => i !== index).map((row, idx) => ({ ...row, line_no: idx + 1 })));
    };

    const buildPayload = () => ({
        plant_id: inputs.plant_id,
        consumption_date: moment(inputs.consumption_date, 'YYYY-MM-DD').format('DD-MM-YYYY'),
        consumption_time: inputs.consumption_time,
        consumption_no: inputs.consumption_no,
        prefix: inputs.prefix,
        consumption_mode: inputs.consumption_mode,
        production_mode: inputs.production_mode,
        from_date: moment(inputs.from_date, 'YYYY-MM-DD').format('DD-MM-YYYY'),
        upto_date: moment(inputs.upto_date, 'YYYY-MM-DD').format('DD-MM-YYYY'),
        customer_id: inputs.customer_id || null,
        consignee_id: inputs.consignee_id || null,
        del_challan_id: inputs.del_challan_id || null,
        total_concrete_qty: parseFloat(inputs.total_concrete_qty) || 0,
        user_remarks: inputs.remarks,
        detail_list: rowsData.map((row, idx) => ({
            line_no: idx + 1,
            product_id: row.product_id,
            material_label: row.material_label,
            design_mix_qty: parseFloat(row.design_mix_qty) || 0,
            consumed_qty: parseFloat(row.consumed_qty) || 0,
            unit: row.unit || 'Kg',
        })),
    });

    const handleSubmit = (event) => {
        event.preventDefault();
        if (!inputs.plant_id) {
            Swal.fire('Validation', 'Plant is required.', 'warning');
            return;
        }
        if (!rowsData.length) {
            Swal.fire('Validation', 'Add at least one material line or load from production.', 'warning');
            return;
        }
        setInProgress(true);
        createConsumption(cookies, buildPayload())
            .then(() => {
                Swal.fire('Saved', 'Consumption note saved successfully.', 'success');
                navigate('/ConsumptionNoteTable');
            })
            .catch((error) => displayError(error?.response?.data || error, 'Save Failed'))
            .finally(() => setInProgress(false));
    };

    const showDateRange = inputs.consumption_mode !== 'batchwise';
    const showCustomer = inputs.consumption_mode === 'customerwise';
    const showSite = inputs.consumption_mode === 'sitewise';
    const showBatch = inputs.consumption_mode === 'batchwise';

    const handleCancel = () => {
        setInputs({
            plant_id: '',
            consumption_date: getCurrentDate(),
            consumption_time: getCurrentTime(),
            consumption_no: '',
            prefix: '',
            consumption_mode: 'datewise',
            production_mode: 'auto',
            from_date: getCurrentDate(),
            upto_date: getCurrentDate(),
            customer_id: '',
            consignee_id: '',
            del_challan_id: '',
            total_concrete_qty: 0,
            remarks: '',
        });
        setRowsData([]);
    };

    return (
        <div id="outer-container" className="App">
            <Sidebar pageWrapId="page-wrap" outerContainerId="outer-container" />
            <ErrorToast />
            <LoadingOverlay active={inProgress} />
            <div id="page-wrap">
                <form onSubmit={handleSubmit}>
                    <FloatingControls
                        tableLink="/ConsumptionNoteTable"
                        formLink="/ConsumptionNote"
                        addNewLabel="Main Form"
                        onCancel={handleCancel}
                        enableCancel
                    />

                    <FormPageLayout
                        moduleLabel="STORE"
                        title="Consumption"
                        subtitle="Select plant and production criteria, load material lines, then save the consumption voucher."
                        modeLabel="CREATE MODE"
                    >
                    <div className="container form-control-panel">
                        <Row>
                            <Col xl={6} lg={12} md={12}>
                                <div className="form-row table-bordered shadow p-2 my-2 border-secondary p-2 mb-3 form-control-panel">
                                    <h4 className="col-sm-11"><b>Production Criteria</b></h4><br /><br />

                                    <label htmlFor="plant_id" className="form-group col-sm-5 text-right">Plant</label>
                                    <select
                                        id="plant_id"
                                        name="plant_id"
                                        className="form-control col-sm-6 browser-default custom-select mandatory-form-control"
                                        value={inputs.plant_id}
                                        onChange={handleChange}
                                        required
                                    >
                                        <option value="">Select Plant</option>
                                        {plants.map((p) => (
                                            <option key={p.value} value={p.value}>{p.label}</option>
                                        ))}
                                    </select><br />

                                    <label htmlFor="consumption_mode" className="form-group col-sm-5 text-right">Consumption Basis</label>
                                    <select
                                        id="consumption_mode"
                                        name="consumption_mode"
                                        className="form-control col-sm-6 mandatory-form-control"
                                        value={inputs.consumption_mode}
                                        onChange={handleChange}
                                    >
                                        {MODE_OPTIONS.map((o) => (
                                            <option key={o.value} value={o.value}>{o.label}</option>
                                        ))}
                                    </select><br />

                                    <label htmlFor="production_mode" className="form-group col-sm-5 text-right">Production Source</label>
                                    <select
                                        id="production_mode"
                                        name="production_mode"
                                        className="form-control col-sm-6 mandatory-form-control"
                                        value={inputs.production_mode}
                                        onChange={handleChange}
                                    >
                                        {PRODUCTION_OPTIONS.map((o) => (
                                            <option key={o.value} value={o.value}>{o.label}</option>
                                        ))}
                                    </select><br />

                                    {showDateRange && (
                                        <>
                                            <label htmlFor="from_date" className="form-group col-sm-5 text-right">From Date</label>
                                            <input
                                                type="date"
                                                id="from_date"
                                                name="from_date"
                                                className="form-control col-sm-6"
                                                value={inputs.from_date}
                                                onChange={handleChange}
                                            /><br />

                                            <label htmlFor="upto_date" className="form-group col-sm-5 text-right">Upto Date</label>
                                            <input
                                                type="date"
                                                id="upto_date"
                                                name="upto_date"
                                                className="form-control col-sm-6"
                                                value={inputs.upto_date}
                                                onChange={handleChange}
                                            /><br />
                                        </>
                                    )}

                                    {showCustomer && (
                                        <>
                                            <label className="form-group col-sm-5 text-right">Customer</label>
                                            <div className="col-sm-6 p-0 mb-2">
                                                <Select
                                                    options={customers}
                                                    value={customers.find((c) => c.value === inputs.customer_id) || null}
                                                    onChange={(opt) => handleSelectChange('customer_id', opt)}
                                                    placeholder="Select customer"
                                                    menuPortalTarget={document.body}
                                                    styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                                                />
                                            </div><br />
                                        </>
                                    )}

                                    {showSite && (
                                        <>
                                            <label className="form-group col-sm-5 text-right">Site (Consignee)</label>
                                            <div className="col-sm-6 p-0 mb-2">
                                                <Select
                                                    options={sites}
                                                    value={sites.find((s) => s.value === inputs.consignee_id) || null}
                                                    onChange={(opt) => handleSelectChange('consignee_id', opt)}
                                                    placeholder="Select site"
                                                    menuPortalTarget={document.body}
                                                    styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                                                />
                                            </div><br />
                                        </>
                                    )}

                                    {showBatch && (
                                        <>
                                            <label className="form-group col-sm-5 text-right">Delivery Challan / Batch</label>
                                            <div className="col-sm-6 p-0 mb-2">
                                                <Select
                                                    options={dcOptions}
                                                    value={dcOptions.find((d) => d.value === inputs.del_challan_id) || null}
                                                    onChange={(opt) => handleSelectChange('del_challan_id', opt)}
                                                    placeholder="Select DC / batch"
                                                    menuPortalTarget={document.body}
                                                    styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                                                />
                                            </div><br />
                                        </>
                                    )}

                                    <label className="form-group col-sm-5 text-right" />
                                    <div className="col-sm-6">
                                        <Button type="button" color="info" size="sm" onClick={handleLoadFromProduction}>
                                            Load from Production
                                        </Button>
                                        &nbsp;
                                        <Button type="button" color="secondary" size="sm" onClick={handleAddLine}>
                                            Add Line
                                        </Button>
                                    </div>
                                </div>
                            </Col>

                            <Col xl={6} lg={12} md={12}>
                                <div className="form-row table-bordered shadow p-2 my-2 border-secondary p-2 mb-3 form-control-panel">
                                    <h4 className="col-sm-11"><b>Consumption Note Details</b></h4><br /><br />

                                    <label htmlFor="consumption_no" className="form-group col-sm-4 text-right">Consumption No</label>
                                    <input
                                        type="text"
                                        id="consumption_no"
                                        className="form-control col-sm-7"
                                        value={`${inputs.prefix || ''}/${inputs.consumption_no || ''}`}
                                        disabled
                                        style={{ cursor: 'not-allowed' }}
                                    /><br />

                                    <label htmlFor="consumption_date" className="form-group col-sm-4 text-right">Date</label>
                                    <input
                                        type="date"
                                        id="consumption_date"
                                        name="consumption_date"
                                        className="form-control col-sm-7 mandatory-form-control"
                                        value={inputs.consumption_date}
                                        onChange={handleChange}
                                        required
                                    /><br />

                                    <label htmlFor="consumption_time" className="form-group col-sm-4 text-right">Time</label>
                                    <input
                                        type="time"
                                        id="consumption_time"
                                        name="consumption_time"
                                        className="form-control col-sm-7"
                                        value={inputs.consumption_time}
                                        onChange={handleChange}
                                    /><br />

                                    <label htmlFor="total_concrete_qty" className="form-group col-sm-4 text-right">Total Concrete (cum)</label>
                                    <input
                                        type="number"
                                        id="total_concrete_qty"
                                        name="total_concrete_qty"
                                        className="form-control col-sm-7"
                                        style={{ textAlign: 'right' }}
                                        value={inputs.total_concrete_qty}
                                        onChange={handleChange}
                                    /><br />
                                </div>
                            </Col>
                        </Row>

                        <div className="form-row table-bordered shadow p-2 my-2 border-secondary p-2 mb-3 bg-blue container table-responsive">
                            <br />
                            <table className="table order-list" id="consumptionTable" style={{ textAlign: 'center', borderRadius: '10px', backgroundColor: 'RGB(188,232,253)' }}>
                                <thead>
                                    <tr>
                                        <th>Action</th>
                                        <th>S.No.</th>
                                        <th>Material</th>
                                        <th>Bin / Silo</th>
                                        <th>Design Mix (kg/cum)</th>
                                        <th>Consumed Qty</th>
                                        <th>Unit</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <TableRowsConsumption
                                        rowsData={rowsData}
                                        products={products}
                                        onChange={setRowsData}
                                        onRemove={handleRemoveLine}
                                    />
                                </tbody>
                            </table>
                        </div>

                        <div className="form-row table-bordered shadow p-2 my-2 border-secondary p-2 mb-3 form-control-panel">
                            <label htmlFor="remarks" className="form-group col-sm-2 text-right">Remarks</label>
                            <textarea
                                rows="2"
                                cols="50"
                                className="form-control col-sm-10"
                                id="remarks"
                                name="remarks"
                                onChange={handleChange}
                                value={inputs.remarks}
                            />
                        </div>
                    </div>

                    <div className="footer company-form-footer text-center">
                        <Button type="submit" className="btn btn-twitter company-form-action-btn" style={{ width: '80px', fontWeight: 'bold' }}><i className="fa fa-floppy-o" aria-hidden="true" /> Save</Button>
                        <Button type="button" className="btn btn-twitter company-form-action-btn" style={{ width: '80px', fontWeight: 'bold' }} onClick={handleCancel}><i className="fa fa-ban" aria-hidden="true" /> Cancel</Button>
                        <Button type="button" className="btn btn-twitter company-form-action-btn" style={{ width: '110px', fontWeight: 'bold' }} onClick={() => navigate('/ConsumptionNoteTable')}><i className="fa fa-eye" aria-hidden="true" /> View</Button>
                        <Button type="button" className="btn btn-twitter company-form-action-btn" style={{ width: '80px', fontWeight: 'bold' }} onClick={() => navigate('/Home')}><i className="fa fa-home" aria-hidden="true" /> Home</Button>
                    </div>
                    </FormPageLayout>
                </form>
            </div>
        </div>
    );
}

export default ConsumptionNote;
